import React from 'react'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  InputAdornment,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Tabs,
  TextField,
  Typography,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import SearchIcon from '@mui/icons-material/Search'
import WorkOutlineIcon from '@mui/icons-material/WorkOutline'
import MatchCountBar from './MatchCountBar'
import JobActionsMenu from './JobActionsMenu'
import posthog from 'posthog-js'
import InlineCandidateMatchContent from './InlineCandidateMatchContent'

const STATUS_FILTER_ALL = 'all'
const STATUS_FILTER_ACTIVE = 'active'
const STATUS_FILTER_ARCHIVED = 'archived'

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50]

const COLUMNS = [
  { id: 'title', label: 'Title', sortable: true },
  { id: 'location', label: 'Location', sortable: true },
  { id: 'practiceType', label: 'Type', sortable: true },
  { id: 'status', label: 'Status', sortable: true },
  { id: 'createdAt', label: 'Posted', sortable: true },
  { id: 'matches', label: 'Matches', sortable: true },
  { id: 'actions', label: 'Actions', sortable: false },
]

const formatDate = dateString => {
  if (!dateString) return '—'
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

const isJobArchived = job => Boolean(job.deletedAt)

const getSortValue = (job, orderBy, matchCountsByJobId) => {
  if (orderBy === 'location') return `${job.city || ''}, ${job.state || ''}`
  if (orderBy === 'status') return isJobArchived(job) ? 'Archived' : 'Active'
  if (orderBy === 'matches') return matchCountsByJobId?.[job.id]?.total ?? 0
  return job[orderBy] ?? ''
}

const sortJobs = (jobs, order, orderBy, matchCountsByJobId) => {
  return [...jobs].sort((a, b) => {
    const aValue = getSortValue(a, orderBy, matchCountsByJobId)
    const bValue = getSortValue(b, orderBy, matchCountsByJobId)

    let comparison = 0
    if (aValue < bValue) comparison = -1
    if (aValue > bValue) comparison = 1

    return order === 'desc' ? -comparison : comparison
  })
}

const StatusChip = ({ job }) => {
  const archived = isJobArchived(job)
  return (
    <Chip
      label={archived ? 'Archived' : 'Active'}
      size='small'
      color={archived ? 'default' : 'success'}
      variant={archived ? 'outlined' : 'filled'}
    />
  )
}

const EmployerJobsTable = ({
  jobs = [],
  isLoading,
  totalJobs = 0,
  page,
  rowsPerPage,
  searchText,
  statusFilter,
  onSearchChange,
  onStatusFilterChange,
  onPageChange,
  onRowsPerPageChange,
  onJobSelect,
  selectedJobId,
  onEditJob,
  onArchiveJob,
  matchCountsByJobId = {},
  matchedCandidates = [],
  isLoadingCandidates,
  onViewFullList,
  isPaywalled,
  onUpgrade,
  currentEmployerUserId,
  hasPostedFreeJob,
  onCreateJob,
}) => {
  const theme = useTheme()

  const [order, setOrder] = React.useState('desc')
  const [orderBy, setOrderBy] = React.useState('createdAt')

  const handleRequestSort = columnId => {
    const isAsc = orderBy === columnId && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(columnId)
  }

  const sortedJobs = React.useMemo(
    () => sortJobs(jobs, order, orderBy, matchCountsByJobId),
    [jobs, order, orderBy, matchCountsByJobId],
  )

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <Box sx={{ px: 2, pt: 2, pb: 1 }}>
        <TextField
          value={searchText}
          onChange={event => onSearchChange(event.target.value)}
          placeholder='Search by title, location, or specialty...'
          size='small'
          fullWidth
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <SearchIcon fontSize='small' color='action' />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box sx={{ borderBottom: `1px solid ${theme.palette.divider}`, px: 2 }}>
        <Tabs
          value={statusFilter}
          onChange={(event, newValue) => {
            posthog.capture('employer_jobs_status_filter_changed', { status: newValue })
            onStatusFilterChange(newValue)
          }}
          textColor='primary'
          indicatorColor='primary'
        >
          <Tab label='All' value={STATUS_FILTER_ALL} />
          <Tab label='Active' value={STATUS_FILTER_ACTIVE} />
          <Tab label='Archived' value={STATUS_FILTER_ARCHIVED} />
        </Tabs>
      </Box>

      <TableContainer>
        <Table stickyHeader aria-label='employer jobs table'>
          <TableHead>
            <TableRow>
              {COLUMNS.map(column => (
                <TableCell
                  key={column.id}
                  sortDirection={orderBy === column.id ? order : false}
                  sx={{
                    '&.MuiTableCell-stickyHeader': {
                      backgroundColor: theme.palette.secondary.light,
                    },
                  }}
                >
                  {column.sortable ? (
                    <TableSortLabel
                      active={orderBy === column.id}
                      direction={orderBy === column.id ? order : 'asc'}
                      onClick={() => handleRequestSort(column.id)}
                    >
                      <Typography variant='body2' fontWeight={600}>
                        {column.label}
                      </Typography>
                    </TableSortLabel>
                  ) : (
                    <Typography variant='body2' fontWeight={600}>
                      {column.label}
                    </Typography>
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedJobs.map(job => (
              <React.Fragment key={job.id}>
                <TableRow
                  hover
                  onClick={() => {
                    posthog.capture('employer_job_selected_in_table', {
                      job_id: job.id,
                      job_title: job.title,
                    })
                    onJobSelect(job)
                  }}
                  selected={job.id === selectedJobId}
                  sx={{
                    cursor: 'pointer',
                    '&.Mui-selected': {
                      backgroundColor: theme.palette.action.selected,
                    },
                    '&.Mui-selected:hover': {
                      backgroundColor: theme.palette.action.selected,
                    },
                  }}
                >
                  <TableCell>
                    <Typography variant='body2' fontWeight={500}>
                      {job.title}
                    </Typography>
                    {job.specialty && (
                      <Chip
                        label={job.specialty}
                        size='small'
                        variant='outlined'
                        sx={{ mt: 0.5, height: 20, fontSize: '0.7rem' }}
                      />
                    )}
                    {currentEmployerUserId && (
                      <Typography variant='caption' color='text.disabled' sx={{ display: 'block', mt: 0.5 }}>
                        {job.employerUserId === currentEmployerUserId
                          ? 'Posted by you'
                          : 'Posted on your behalf'}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant='body2'>
                      {[job.city, job.state].filter(Boolean).join(', ') || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant='body2'>{job.practiceType || '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    <StatusChip job={job} />
                  </TableCell>
                  <TableCell>
                    <Typography variant='body2'>{formatDate(job.createdAt)}</Typography>
                  </TableCell>
                  <TableCell>
                    {!currentEmployerUserId || job.employerUserId === currentEmployerUserId ? (
                      <Box
                        onClick={event => {
                          event.stopPropagation()
                          onJobSelect(job)
                        }}
                        sx={{ display: 'inline-flex', cursor: 'pointer' }}
                      >
                        <MatchCountBar matchCounts={matchCountsByJobId[job.id]} />
                      </Box>
                    ) : (
                      <Typography variant='body2' color='text.disabled'>—</Typography>
                    )}
                  </TableCell>
                  <TableCell onClick={event => event.stopPropagation()}>
                    <JobActionsMenu
                      job={job}
                      onEditJob={onEditJob}
                      onArchiveJob={onArchiveJob}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell
                    colSpan={COLUMNS.length}
                    sx={{ py: 0, px: 0, borderBottom: job.id === selectedJobId ? undefined : 'none' }}
                  >
                    <Collapse in={job.id === selectedJobId} timeout='auto' unmountOnExit>
                      <Box
                        sx={{
                          backgroundColor: theme.palette.action.hover,
                          borderLeft: `3px solid ${theme.palette.primary.main}`,
                          px: 3,
                          py: 2,
                        }}
                      >
                        <InlineCandidateMatchContent
                          job={job}
                          matchedCandidates={matchedCandidates}
                          isLoadingCandidates={isLoadingCandidates}
                          onViewFullList={onViewFullList}
                          isPaywalled={isPaywalled}
                          onUpgrade={onUpgrade}
                          matchCounts={matchCountsByJobId[job.id]}
                        />
                      </Box>
                    </Collapse>
                  </TableCell>
                </TableRow>
              </React.Fragment>
            ))}
            {sortedJobs.length === 0 && (
              <TableRow>
                <TableCell colSpan={COLUMNS.length} align='center' sx={{ py: 8 }}>
                  {searchText || statusFilter !== STATUS_FILTER_ALL ? (
                    <Typography variant='body2' color='text.secondary'>
                      No jobs match your current filters.
                    </Typography>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5 }}>
                      <WorkOutlineIcon sx={{ fontSize: 48, color: 'text.disabled' }} />
                      <Typography variant='h6' sx={{ fontWeight: 600 }}>
                        No jobs posted yet
                      </Typography>
                      {!hasPostedFreeJob ? (
                        <>
                          <Typography variant='body2' color='text.secondary'>
                            Your first job posting is free — start attracting candidates today.
                          </Typography>
                          <Button
                            variant='contained'
                            size='large'
                            onClick={onCreateJob}
                            sx={{ mt: 1, textTransform: 'none', fontWeight: 600 }}
                          >
                            Post Your First Job — It's Free
                          </Button>
                        </>
                      ) : (
                        <>
                          <Typography variant='body2' color='text.secondary'>
                            Subscribe to a plan to post additional jobs.
                          </Typography>
                          <Button
                            variant='contained'
                            size='large'
                            onClick={onUpgrade}
                            sx={{ mt: 1, textTransform: 'none', fontWeight: 600 }}
                          >
                            View Plans
                          </Button>
                        </>
                      )}
                    </Box>
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        component='div'
        count={totalJobs}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
      />
    </Paper>
  )
}

export default EmployerJobsTable
